import type { MultilineElementTransformer } from '@lexical/markdown';
import {
  $createChartNode,
  $isChartNode,
  ChartNode,
} from '../../../SubjectViewer/Modules/AITools/DynamicCharts/ChartNode';
import { parseChartSpec } from '../../../SubjectViewer/Modules/AITools/DynamicCharts/schema';

const COMMENT_CLOSE = '-->';

/**
 * Finds the line index of the closing `-->` for an HTML comment that starts
 * on `startLineIndex`. Returns null if the comment is unterminated so we do
 * not swallow the rest of the document.
 */
export const findHtmlCommentEndLine = (
  lines: string[],
  startLineIndex: number,
): number | null => {
  const startLine = lines[startLineIndex] ?? '';
  const openIndex = startLine.indexOf('<!--');
  if (openIndex === -1) {
    return null;
  }
  if (startLine.slice(openIndex + 4).includes(COMMENT_CLOSE)) {
    return startLineIndex;
  }
  for (let i = startLineIndex + 1; i < lines.length; i++) {
    if (lines[i].includes(COMMENT_CLOSE)) {
      return i;
    }
  }
  return null;
};

const extractChartJson = (
  lines: string[],
  startLineIndex: number,
  endLineIndex: number,
): string => {
  const startLine = lines[startLineIndex] ?? '';
  const afterOpen = startLine.replace(/^[ \t]*<!--chart\b/, '');

  if (startLineIndex === endLineIndex) {
    const closeIndex = afterOpen.indexOf(COMMENT_CLOSE);
    return (
      closeIndex === -1 ? afterOpen : afterOpen.slice(0, closeIndex)
    ).trim();
  }

  const lastLine = lines[endLineIndex] ?? '';
  const closeIndex = lastLine.indexOf(COMMENT_CLOSE);
  const lastBody = closeIndex === -1 ? lastLine : lastLine.slice(0, closeIndex);

  return [afterOpen, ...lines.slice(startLineIndex + 1, endLineIndex), lastBody]
    .join('\n')
    .trim();
};

const specFromJson = (jsonContent: string) => {
  try {
    return parseChartSpec(JSON.parse(jsonContent));
  } catch {
    return null;
  }
};

/**
 * Transformer for `<!--chart ... -->` comment blocks.
 */
export const CHART: MultilineElementTransformer = {
  get dependencies() {
    return [ChartNode];
  },

  regExpStart: /^[ \t]*<!--chart\b/,

  handleImportAfterStartMatch: ({ lines, rootNode, startLineIndex }) => {
    const endLineIndex = findHtmlCommentEndLine(lines, startLineIndex);
    if (endLineIndex === null) {
      return null;
    }

    const jsonContent = extractChartJson(lines, startLineIndex, endLineIndex);
    rootNode.append($createChartNode(specFromJson(jsonContent)));
    return [true, endLineIndex];
  },

  replace: () => {
    // Not called: handleImportAfterStartMatch handles import.
  },

  export: node => {
    if (!$isChartNode(node)) {
      return null;
    }
    const spec = node.getSpec();
    if (!spec) {
      return null;
    }
    return `<!--chart\n${JSON.stringify(spec)}\n-->`;
  },

  type: 'multiline-element',
};
